import { Formik, Form } from "formik";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { DetailField } from "@/core/components/detail-field";
import { FormInput } from "@/core/components/form-input";
import { MaskedFormInput } from "@/core/components/masked-form-input";
import { AddressInput } from "@/core/components/ui/address-input";
import { Button } from "@/core/components/ui/button";
import { DateTimePicker } from "@/core/components/ui/date-time-picker";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Separator } from "@/core/components/ui/separator";
import { SimpleSidebar } from "@/core/components/ui/simple-sidebar";
import { Stack } from "@/core/components/ui/stack";
import {
  Tabs,
  TabsContent,
  TabsListWithIndicator,
  TabsTrigger,
} from "@/core/components/ui/tabs";
import { Textarea } from "@/core/components/ui/textarea";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useBusiness } from "@/core/business";
import { numberOnlyMask } from "@/core/masks";
import { UpdateAddressParams } from "@/core/types/address";
import { getAddressFromUpdateAddressParams } from "@/core/utils/address";
import { formatDateTimeWithZone } from "@/core/utils/date";
import { CUSTOMER_STATUS } from "@/customer/constants";
import { getCustomerNameAndPhone } from "@/customer/utils";
import { CloseConfirmationModal } from "@/referrals/common/close-confirmation-modal";
import { ReferralActivityList } from "@/referrals/common/referral-activity-list";
import { SidebarHeader } from "@/referrals/common/sidebar-header";
import {
  REFERRAL_COLUMN_TO_STATUS,
  REFERRAL_COLUMN,
} from "@/referrals/constants";
import type {
  IReferral,
  ReferralColumn,
  ReferralUpdateParams,
} from "@/referrals/referral";

interface FormValues {
  service_needed: string;
  address: UpdateAddressParams;
  additional_info: string;
  service_titan_job_id: string;
  appointment_date: string | null;
}

const serviceTitanJobIdMaskOptions = numberOnlyMask();

const validationSchema = Yup.object().shape({
  service_needed: Yup.string(),
  service_titan_job_id: Yup.string(),
  address: Yup.object()
    .shape({
      line_one: Yup.string(),
      line_two: Yup.string(),
      city: Yup.string(),
      state: Yup.string(),
      zip_code: Yup.string(),
    })
    .test(
      "all-or-none",
      "Please complete all address fields (Address, City, State, and ZIP) or leave them all empty",
      (value) => {
        if (!value) return true;
        const fields: (keyof typeof value)[] = [
          "line_one",
          "city",
          "state",
          "zip_code",
        ];
        const filledFields = fields.filter((f) => value[f]);
        return (
          filledFields.length === 0 || filledFields.length === fields.length
        );
      },
    ),
  additional_info: Yup.string(),
  appointment_date: Yup.date().nullable(),
});

interface KanbanSidebarProps {
  referral: IReferral | null;
  onClose: () => void;
  onUpdate: (values: Partial<ReferralUpdateParams>) => void;
  onNextColumn: (column: ReferralColumn, referral: IReferral) => void;
  onLost: () => void;
  onPayout: () => void;
  onDelete: () => void;
}

export const KanbanSidebar = ({
  referral,
  onClose,
  onUpdate,
  onNextColumn,
  onLost,
  onPayout,
  onDelete,
}: KanbanSidebarProps) => {
  const navigate = useNavigate();
  const { businessId, selectedBusiness } = useBusiness();
  const { updateSidebar } = useBusinessSettingsSidebar();

  const isServiceTitanEnabled =
    selectedBusiness?.service_titan_enabled || false;

  const [showLostConfirmationModal, setShowLostConfirmationModal] =
    useState<IReferral | null>(null);
  const [activeTab, setActiveTab] = useState("details");

  const initialValues = useMemo<FormValues>(
    () => ({
      service_needed: referral?.service_needed || "",
      service_titan_job_id: referral?.service_titan_job_id?.toString() || "",
      address: referral?.address || {
        line_one: "",
        line_two: "",
        city: "",
        state: "",
        zip_code: "",
      },
      additional_info: referral?.additional_info || "",
      appointment_date: referral?.appointment_date || null,
    }),
    [referral],
  );

  const ActionButton = useMemo(() => {
    const ButtonComponent = ({
      className,
      size = "sm",
    }: {
      className?: string;
      size?: "sm" | "default" | "lg";
    }) => {
      switch (referral?.status) {
        case REFERRAL_COLUMN_TO_STATUS.new:
          return (
            <Button
              className={className}
              size={size}
              type="button"
              onClick={() => onNextColumn(REFERRAL_COLUMN.inProgress, referral)}
              fullWidth
            >
              Accept
            </Button>
          );
        case REFERRAL_COLUMN_TO_STATUS.inProgress:
          return (
            <Button
              className={className}
              size={size}
              type="button"
              onClick={() => onNextColumn(REFERRAL_COLUMN.scheduled, referral)}
              fullWidth
            >
              Move to scheduled
            </Button>
          );
        case REFERRAL_COLUMN_TO_STATUS.scheduled:
          return (
            <Button
              className={className}
              size={size}
              type="button"
              onClick={() => onNextColumn(REFERRAL_COLUMN.closed, referral)}
              fullWidth
            >
              Move to closed
            </Button>
          );
        case REFERRAL_COLUMN_TO_STATUS.closed:
          return (
            <Button
              className={className}
              size={size}
              type="button"
              onClick={onPayout}
              fullWidth
            >
              Pay advocate
            </Button>
          );
        default:
          return null;
      }
    };
    return ButtonComponent;
  }, [referral, onNextColumn]);

  return (
    <>
      <SimpleSidebar data-cy="referral-kanban-sidebar">
        {referral && (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values: FormValues, { resetForm }) => {
              const updateParams: Partial<ReferralUpdateParams> = {
                ...values,
                address: getAddressFromUpdateAddressParams(values.address),
                service_titan_job_id: values.service_titan_job_id
                  ? parseInt(values.service_titan_job_id)
                  : null,
                appointment_date: values.appointment_date,
              };

              onUpdate(updateParams);
              resetForm({ values });
            }}
            enableReinitialize
            className="h-full"
          >
            {({ values, setFieldValue, getFieldProps, dirty, resetForm }) => (
              <Stack gap="none" fullWidth className="h-full">
                <Form className="flex h-full w-full flex-col">
                  <SidebarHeader
                    referral={referral}
                    actionButton={<ActionButton className="flex-1" />}
                    onClose={onClose}
                    onPayout={onPayout}
                    onLost={() => setShowLostConfirmationModal(referral)}
                    onMoveToPaid={() => onUpdate({ status: "PAID" })}
                    onDelete={onDelete}
                  />
                  <Tabs
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="flex flex-1 flex-col overflow-hidden pb-6 pl-6"
                  >
                    <TabsListWithIndicator className="w-full">
                      <TabsTrigger
                        value="details"
                        className="w-full"
                        onClick={() => setActiveTab("details")}
                      >
                        Details
                      </TabsTrigger>
                      <TabsTrigger
                        value="history"
                        className="w-full"
                        onClick={() => setActiveTab("history")}
                      >
                        History
                      </TabsTrigger>
                    </TabsListWithIndicator>
                    <TabsContent value="details">
                      <ScrollArea className="h-full w-full">
                        <Stack gap="lg" className="py-6 pr-6" fullWidth>
                          <FormInput
                            label="Services needed"
                            placeholder="Services needed"
                            {...getFieldProps("service_needed")}
                            fullWidth
                          />

                          <AddressInput
                            value={values.address}
                            onChange={(value) =>
                              setFieldValue("address", value)
                            }
                          />
                          {referral.status ===
                            REFERRAL_COLUMN_TO_STATUS.scheduled && (
                            <DateTimePicker
                              name="appointment_date"
                              label="Appointment Date"
                              placeholder="mm/dd/yyyy"
                              fullWidth
                              value={
                                values.appointment_date
                                  ? new Date(values.appointment_date)
                                  : null
                              }
                              onChange={(date) => {
                                setFieldValue(
                                  "appointment_date",
                                  date ? date.toISOString() : null,
                                );
                              }}
                            />
                          )}
                          {isServiceTitanEnabled && (
                            <MaskedFormInput
                              name="service_titan_job_id"
                              label="Service Titan Job ID"
                              placeholder="Service Titan Job ID"
                              value={values.service_titan_job_id}
                              onChange={(value) =>
                                setFieldValue("service_titan_job_id", value)
                              }
                              maskOptions={serviceTitanJobIdMaskOptions}
                              fullWidth
                            />
                          )}
                          <Separator />
                          <Textarea
                            placeholder="Notes"
                            label="Notes"
                            {...getFieldProps("additional_info")}
                          />
                          <Stack direction="row" gap="sm">
                            <Button
                              type="submit"
                              variant="secondary"
                              size="sm"
                              disabled={!dirty}
                            >
                              Save changes
                            </Button>
                            <Button
                              variant="text"
                              size="sm"
                              type="button"
                              disabled={!dirty}
                              onClick={() =>
                                resetForm({ values: initialValues })
                              }
                            >
                              Cancel
                            </Button>
                          </Stack>
                          <Separator />
                          <Stack gap="lg" fullWidth className="px-4">
                            <DetailField
                              label="Advocate"
                              message={getCustomerNameAndPhone(
                                referral?.advocate,
                              )}
                              onClick={() => {
                                navigate(
                                  `/business-settings/${businessId}/customers/${referral?.advocate?.status === CUSTOMER_STATUS.ACTIVE ? "advocates" : "leads"}`,
                                );
                                updateSidebar({
                                  section: SIDEBAR_SECTIONS.advocates,
                                  mode: SIDEBAR_MODES.view,
                                  selectedItemId: referral?.advocate?.id,
                                  isOpen: true,
                                });
                              }}
                              extraInfo="Customer who sent the referral (the one you pay out)"
                            />
                            <DetailField
                              label="Referral SMS"
                              message={referral.text_message?.body || "No SMS"}
                              subMessage={
                                referral.created_at
                                  ? formatDateTimeWithZone(referral.created_at)
                                  : "No from"
                              }
                              extraInfo={`Original referral message sent to ${referral.business.name} hotline by advocate`}
                            />
                          </Stack>
                        </Stack>
                      </ScrollArea>
                    </TabsContent>
                    <TabsContent value="history">
                      <ScrollArea className="h-full">
                        <ReferralActivityList referral={referral} />
                      </ScrollArea>
                    </TabsContent>
                  </Tabs>
                </Form>
              </Stack>
            )}
          </Formik>
        )}
      </SimpleSidebar>

      <CloseConfirmationModal
        referral={showLostConfirmationModal}
        onOpenChange={setShowLostConfirmationModal}
        onConfirm={onLost}
      />
    </>
  );
};
